/**
 * @file     BuyerSmartSearchBlock.tsx
 * @purpose  Saved-search band for the programmatic pages: the kit's band alert capture with the page's own heading, one line on what you get, and a plain link to the detailed buyer search. No LLM (ADR-009).
 */
'use client';

import Link from 'next/link';
import Box from '@mui/material/Box';
import { flowColors, flowFocusRing, flowRadius } from '@/features/lead-flows/components/flowKitTokens';
import { AlertSignupFlow } from './AlertSignupFlow';
import type { ShortlistIntent } from './shortlistIntent';

export const SAVED_SEARCH_EYEBROW = 'Saved search';
export const SAVED_SEARCH_DEFAULT_HEADING = 'Homes that fit, sent to your dashboard.';

export function BuyerSmartSearchBlock({
  intent,
  locationTag,
  place,
  /** Where the full buyer flow should attribute this entry. */
  flowSource,
  heading = SAVED_SEARCH_DEFAULT_HEADING,
}: {
  intent: ShortlistIntent;
  locationTag: string;
  /** Place name used in the flow copy. */
  place: string;
  flowSource: string;
  heading?: string;
}) {
  return (
    <Box sx={{ my: { xs: 5, md: 7 } }}>
      <AlertSignupFlow
        variant="band"
        intent={intent}
        locationTag={locationTag}
        place={place}
        eyebrow={SAVED_SEARCH_EYEBROW}
        title={heading}
        sub="Get new listings that fit, by email."
        cta="Save search"
        headingComponent="h2"
        footer={
          <Box
            component={Link}
            href={`/deal-finder/start?flow=buyer&source=${flowSource}`}
            sx={{
              alignSelf: 'flex-start',
              display: 'inline-flex',
              alignItems: 'center',
              minHeight: 44,
              fontSize: 15,
              fontWeight: 500,
              color: flowColors.link,
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
              borderRadius: `${flowRadius.sm}px`,
              '&:hover': { color: flowColors.linkHover },
              ...flowFocusRing,
            }}
          >
            Set up a detailed search
          </Box>
        }
      />
    </Box>
  );
}
